import React from 'react';
import { useNavigate } from 'react-router-dom';

import type { AgentInfo } from '../lib/agents';
import { cn } from '../lib/utils';
import { Button } from './ui';

interface AgentCardProps {
  agent: AgentInfo;
  /** Render a larger card (used on AgentHubPage) */
  size?: 'compact' | 'full';
  /** Override the CTA label */
  ctaLabel?: string;
  className?: string;
}

/**
 * Shared agent card component — used on both the Landing Page and Agent Hub.
 * Displays agent name, colour accent, core message, and a CTA button.
 */
export function AgentCard({
  agent,
  size = 'compact',
  ctaLabel = 'Start a Conversation',
  className,
}: AgentCardProps): React.JSX.Element {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/agents/${agent.id}/chat`);
  };

  const isCompact = size === 'compact';

  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border border-border bg-surface-raised shadow-sm transition-shadow hover:shadow-md',
        'border-l-4',
        agent.borderClass,
        isCompact ? 'p-5' : 'p-6',
        className,
      )}
    >
      {/* Agent header */}
      <div className="mb-2 flex items-baseline gap-2">
        <h3 className={cn('font-semibold text-text-primary', isCompact ? 'text-lg' : 'text-xl')}>
          {agent.name}
        </h3>
        {!isCompact && (
          <span className="text-sm font-medium" style={{ color: agent.colour }}>{agent.colourLabel}</span>
        )}
      </div>

      {/* Core message */}
      <p className="mb-2 text-sm font-medium text-text-secondary">{agent.coreMessage}</p>

      {/* Description */}
      <p className={cn('text-text-muted', isCompact ? 'mb-4 text-sm' : 'mb-5 text-sm leading-relaxed')}>
        {agent.description}
      </p>

      {/* Focus areas (full size only) */}
      {!isCompact && (
        <div className="mb-5 flex flex-wrap gap-2">
          {agent.focusAreas.map((area) => (
            <span
              key={area}
              className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium text-text-secondary', agent.bgLightClass)}
            >
              {area}
            </span>
          ))}
        </div>
      )}

      {/* CTA */}
      <div className="mt-auto">
        <Button
          variant={agent.buttonVariant}
          size={isCompact ? 'sm' : 'md'}
          onClick={handleClick}
          className="w-full"
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}
